import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import type {
  ClientApiV1CourseUserUserIdResponse,
  ClientApiV1CoursesCourseIdLikeUpdateVariables,
} from '../../resources/@types/uccSchema';
import type { UseReactionMutate } from '../components/pages/profile/CourseItem/CourseItem';
import { getInstructorIdCourseInfinityQueryKey } from '../queries/useCourseUserUserIdQuery';
import { useCourseCourseIdLikeUpdateMutation } from './useCourseCourseIdLikeUpdateMutation';

export const useCoursesPageReactionMutate: UseReactionMutate = ({ courseId, profileId }) => {
  const queryClient = useQueryClient();
  const courseUserUserIdQuery = getInstructorIdCourseInfinityQueryKey({
    userId: String(profileId),
  });

  const { mutate } = useCourseCourseIdLikeUpdateMutation(
    courseId,
    async (variables) => {
      await queryClient.cancelQueries(courseUserUserIdQuery);

      const previousCoursesResponse =
        queryClient.getQueryData<InfiniteData<ClientApiV1CourseUserUserIdResponse>>(
          courseUserUserIdQuery,
        );

      const previousCoursesPages = previousCoursesResponse?.pages;

      if (previousCoursesPages) {
        const newCoursesPages = previousCoursesPages.map((previousCoursePage) => {
          if (previousCoursePage?.data?.items) {
            const newCourses = previousCoursePage.data.items.map((previousCourse) => {
              if (previousCourse.id === courseId) {
                return {
                  ...previousCourse,
                  hasMyReaction: variables.status === 'LIKE',
                };
              }

              return previousCourse;
            });

            return {
              ...previousCoursePage,
              data: {
                ...previousCoursePage.data,
                items: newCourses,
              },
            };
          }

          return previousCoursePage;
        });

        queryClient.setQueryData<InfiniteData<ClientApiV1CourseUserUserIdResponse>>(
          courseUserUserIdQuery,
          {
            ...previousCoursesResponse,
            pages: newCoursesPages,
          },
        );
      }

      return { previousCoursesResponse };
    },
    (_error, _variables, context) => {
      if (context?.previousCoursesResponse) {
        queryClient.setQueryData<InfiniteData<ClientApiV1CourseUserUserIdResponse>>(
          courseUserUserIdQuery,
          context.previousCoursesResponse,
        );
      }
    },
  );

  return {
    reactionMutate: ({ status }: ClientApiV1CoursesCourseIdLikeUpdateVariables) => {
      mutate({ status });
    },
  };
};
